import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';

import type {
  ActiveCampaignFieldMapping,
  AhoyPropertyOption,
} from '../../types';
import type { MappingUpdate } from '../EditableMappingRow';
import { MappingSections } from '../MappingSections';

interface WizardFieldsStepProps {
  mappings: ActiveCampaignFieldMapping[];
  availableAhoyProperties: AhoyPropertyOption[];
  savingIds: ReadonlySet<number>;
  errorsById: ReadonlyMap<number, string>;
  isLoading: boolean;
  isError: boolean;
  onUpdate: (mappingId: number, body: MappingUpdate) => void;
}

export function WizardFieldsStep({
  mappings,
  availableAhoyProperties,
  savingIds,
  errorsById,
  isLoading,
  isError,
  onUpdate,
}: WizardFieldsStepProps) {
  if (isLoading) {
    return (
      <div className='flex justify-center py-10'>
        <LoadingIndicator size='sm' />
      </div>
    );
  }

  // Only block on a hard error when there's nothing to show. A transient
  // refetch failure must not tear down already-loaded mappings — keep the
  // cached rows editable and let the row-level error surfacing handle failures.
  if (mappings.length === 0 && isError) {
    return (
      <p className='text-sm text-[var(--color-text-error-primary)]'>
        Couldn&apos;t load field mappings — please try again.
      </p>
    );
  }

  if (mappings.length === 0) {
    return (
      <p className='text-sm text-[var(--color-text-secondary)]'>
        No field mappings found.
      </p>
    );
  }

  return (
    <MappingSections
      mappings={mappings}
      availableAhoyProperties={availableAhoyProperties}
      savingIds={savingIds}
      errorsById={errorsById}
      onUpdate={onUpdate}
    />
  );
}
